import { isEqual, isObject } from 'lodash'

import {
  AccountActivityApi,
  CustomerApi,
  patchNotificationsBySection,
  patchUserProperties} from '../api'
import C from '../components/NotificationsDefaultSettings/constants'
import { SectionNotificationSetting } from '../typings/conversations'

import { captureException } from './sentry'

interface PatchOperation {
  operation: string
  property: string
  value: any
}

// This is an array of keys whose values are coerced from false to 'nothing' on save.
const FALSE_IS_NOTHING_KEYS = ['humane-notifications-email']
const isNothingValueField = (key: string) => FALSE_IS_NOTHING_KEYS.includes(key)

function getIntegrationPayload(
  newIntegrations: Record<string, any>,
  integrationValues: Record<string, any>
) {
  const integrationPayload: Record<string, boolean | null> = {
    topLevelFirstErrorEnabled: newIntegrations.topLevelFirstErrorEnabled
  }
  C.INTEGRATION_OPTIONS.forEach(({ value: property }) => {
    integrationPayload[property] = integrationValues.includes(property) || null
  })
  return integrationPayload
}

export function getUnsavedChanges(
  initialValues: Record<string, any>,
  formikValues: Record<string, any>
) {
  let count = 0
  const hasNothingValue = (changedValue: any, originalValue: any) =>
    changedValue === 'nothing' && !originalValue

  function changes(object: Record<string, any>, base: Record<string, any>) {
    Object.keys(object).forEach(key => {
      const value = object[key]
      if (
        !isEqual(value, base[key]) &&
        !(isNothingValueField(key) && hasNothingValue(value, base[key]))
      ) {
        count = isObject(value) && isObject(base[key]) ? changes(value, base[key]) : count + 1
      }
    })
    return count
  }
  return changes(initialValues, formikValues)
}

export async function updateSubscriptionNotifications(subscriptionPayload: Record<string, any>) {
  try {
    await patchNotificationsBySection(subscriptionPayload)
  } catch (subscriptionError) {
    captureException(subscriptionError, {
      tags: { location: 'patchNotificationsBySection-notificationSettingsSubmit' }
    })
  }
}

export async function updatePropertiesNotifications(
  currentUserId: number,
  propertyOperations: PatchOperation[]
) {
  try {
    if (propertyOperations.length > 0) {
      await patchUserProperties(currentUserId, propertyOperations)
    }
  } catch (userPropertiesError) {
    captureException(userPropertiesError, {
      tags: { location: 'patchUserProperties-notificationSettingsSubmit' }
    })
  }
}

export async function updateIntegrationNotifications(
  updatedIntegrationSettings: Record<string, any>,
  integrationValues: Record<string, any>
) {
  try {
    const integrationPayload = getIntegrationPayload(updatedIntegrationSettings, integrationValues)
    await CustomerApi.patch(`integrations/admin/notification/error`, integrationPayload)
  } catch (integrationsError) {
    captureException(integrationsError, {
      tags: { location: 'integrations-notificationSettingsSubmit' }
    })
  }
}

function getSavedFilterPayload(
  updatedSavedFilters: Record<string, boolean>,
  currentUserId: number
) {
  const subscriptions = Object.keys(updatedSavedFilters)
    .map(fieldName => {
      const [type, id] = fieldName.split('-')
      const value = updatedSavedFilters[fieldName]
      return {
        userId: currentUserId,
        savedFilterSetId: id,
        hasEmail: type === C.SAVED_FILTER_TYPE.EMAIL ? value : false,
        hasDesktop: type === C.SAVED_FILTER_TYPE.PUSH ? value : false,
        hasMobile: type === C.SAVED_FILTER_TYPE.PUSH ? value : false
      }
    })
    .reduce((acc: any[], filterSet: Record<string, any>) => {
      const foundFilterSet = acc.find(
        (set: any) => set.savedFilterSetId === filterSet.savedFilterSetId
      )
      if (foundFilterSet) {
        foundFilterSet.hasEmail = foundFilterSet?.hasEmail || filterSet?.hasEmail
        foundFilterSet.hasDesktop = foundFilterSet?.hasDesktop || filterSet?.hasDesktop
        foundFilterSet.hasMobile = foundFilterSet?.hasMobile || filterSet?.hasMobile
        return acc
      }
      return [...acc, filterSet]
    }, [])
  return subscriptions
}

export async function updateSavedFilterNotifications(
  currentUserId: number,
  updatedSavedFilters: Record<string, any>
) {
  try {
    const subscriptions = getSavedFilterPayload(updatedSavedFilters, currentUserId)
    await AccountActivityApi.post('/v2/filter_sets/subscription', subscriptions)
  } catch (savedFilterError) {
    captureException(savedFilterError, {
      tags: { location: 'savedFilter-notificationSettingsSubmit' }
    })
  }
}

function transformFalseValuesToNothing([key, value]: [string, any]) {
  if (isNothingValueField(key)) {
    return [key, !value ? 'nothing' : value]
  }
  return [key, value]
}

function getFormattedProperties(
  newProperties: Record<string, any>,
  initialProperties: Record<string, any>
) {
  return Object.entries(newProperties)
    .filter(([key, value]) => initialProperties[key] !== value && value !== null)
    .map(transformFalseValuesToNothing)
}

export function getPropertyOperations(
  newProperties: Record<string, any>,
  initialProperties: Record<string, any>
) {
  const updatedPropertyFields = getFormattedProperties(newProperties, initialProperties)

  return updatedPropertyFields.map(([key, value]) => ({
    operation: 'set',
    property: key,
    value
  }))
}

export function getFormattedSubscriptions(
  newSubscriptions: Record<string, any>,
  initialSubscriptions: Record<string, any>
) {
  const subscriptionPayload: Record<string, SectionNotificationSetting> = {
    ...initialSubscriptions
  }
  Object.entries(newSubscriptions).forEach(
    ([subscriptionId, allNotificationsForSubscriptionEnabled]) => {
      subscriptionPayload[subscriptionId] = {
        // @ts-ignore
        allNotificationsForSubscriptionEnabled,
        subscriptionId
      }
    }
  )
  return subscriptionPayload
}
